import { PERMISSIONS, USER_STATUSES, type UserStatus } from "@procuremate/shared";
import { UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { useHasPermission } from "@/app/require-permission";
import { Button } from "@/components/ui/button";
import { SelectField } from "@/components/ui/select-field";
import { TextField } from "@/components/ui/text-field";
import { Card } from "./admin-layout";
import { USERS_PAGE_SIZE, useUsers } from "./api";
import { StatusBadge } from "./status-badge";

const SEARCH_DELAY_MS = 300;

/** User list with search, status filter and paging; state lives in the URL (shareable, back-button safe). */
export function UsersPage() {
  const { t, i18n } = useTranslation();
  const canManage = useHasPermission(PERMISSIONS.USERS_MANAGE);
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const statusParam = params.get("status") ?? "";
  const status = (USER_STATUSES as readonly string[]).includes(statusParam) ? (statusParam as UserStatus) : "";
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [search, setSearch] = useState(q);

  // Typing updates the URL after a short pause (one request per pause, not per keystroke).
  useEffect(() => {
    if (search.trim() === q) return;
    const timer = setTimeout(() => update({ q: search.trim(), page: "" }), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  });

  function update(changes: Record<string, string>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  }

  const users = useUsers({ q, status, page });
  const total = users.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / USERS_PAGE_SIZE));
  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" });

  return (
    <Card
      title={t("admin.users.title")}
      headingLevel={1}
      actions={
        canManage ? (
          <Link
            to="/admin/users/new"
            className="inline-flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-on-primary hover:bg-primary-hover"
          >
            <UserPlus aria-hidden="true" className="h-4 w-4" />
            {t("admin.users.invite")}
          </Link>
        ) : null
      }
    >
      <div className="mb-4 grid gap-4 sm:grid-cols-[1fr_14rem]">
        <TextField
          id="user-search"
          label={t("admin.users.search")}
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <SelectField
          id="user-status"
          label={t("admin.users.statusFilter")}
          placeholder={t("admin.users.allStatuses")}
          value={status}
          options={USER_STATUSES.map((s) => ({ value: s, label: t(`admin.status.${s}`) }))}
          onChange={(event) => update({ status: event.target.value, page: "" })}
        />
      </div>

      {users.isError ? (
        <p role="alert" className="rounded-sm bg-danger-bg px-3 py-2 text-sm text-danger">
          {t("auth.genericError")}
        </p>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <caption className="sr-only">{t("admin.users.tableCaption")}</caption>
          <thead className="border-b border-border text-fg-muted">
            <tr>
              <th scope="col" className="py-2 pr-4 font-semibold">
                {t("admin.users.name")}
              </th>
              <th scope="col" className="py-2 pr-4 font-semibold">
                {t("admin.users.status")}
              </th>
              <th scope="col" className="hidden py-2 pr-4 font-semibold md:table-cell">
                {t("admin.users.roles")}
              </th>
              <th scope="col" className="hidden py-2 font-semibold lg:table-cell">
                {t("admin.users.lastLogin")}
              </th>
            </tr>
          </thead>
          <tbody>
            {users.data?.items.map((user) => (
              <tr key={user.id} className="border-b border-border last:border-0">
                <td className="py-3 pr-4">
                  <Link to={`/admin/users/${user.id}`} className="font-semibold text-link hover:underline">
                    {user.displayName}
                  </Link>
                  <div className="break-all text-fg-muted">{user.email}</div>
                </td>
                <td className="py-3 pr-4 align-top">
                  <StatusBadge status={user.status} />
                </td>
                <td className="hidden py-3 pr-4 align-top md:table-cell">
                  {user.roles
                    .map((r) => t(`admin.roleNames.${r.roleKey}`, { defaultValue: r.roleName }))
                    .filter((name, index, all) => all.indexOf(name) === index)
                    .join(", ") || "—"}
                </td>
                <td className="hidden py-3 align-top text-fg-muted lg:table-cell">
                  {user.lastLoginAt ? dateFormat.format(new Date(user.lastLoginAt)) : t("admin.users.never")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {users.data && users.data.items.length === 0 ? (
        <p className="py-6 text-center text-fg-muted">{t("admin.users.empty")}</p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-fg-muted">
        <p aria-live="polite">{t("admin.users.count", { count: total })}</p>
        {pages > 1 ? (
          <div className="flex items-center gap-2">
            <Button variant="secondary" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>
              {t("admin.users.previous")}
            </Button>
            <span>{t("admin.users.pageOf", { page, pages })}</span>
            <Button variant="secondary" disabled={page >= pages} onClick={() => update({ page: String(page + 1) })}>
              {t("admin.users.next")}
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
